import React, { useEffect, useRef, useState } from "react";
import { ImageUp, RefreshCw, X } from "lucide-react";
import { REFERENCE_MAX_BYTES, REFERENCE_TYPES } from "./imageCreationService";

/**
 * Reference image picker. The file stays in the browser and is sent with each
 * generate request — nothing is uploaded or stored on the server beforehand.
 * `value` = { file, previewUrl } | null.
 */
export default function ReferenceImageUpload({
  value,
  onChange,
  disabled,
  label = "Reference Image",
  hint = "Upload the menu image you want to edit.",
  testId = "reference-image",
}) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const previewRef = useRef("");

  // Release the last object URL we created, whenever it is replaced or unmounted.
  useEffect(() => {
    if (previewRef.current && previewRef.current !== value?.previewUrl) {
      URL.revokeObjectURL(previewRef.current);
    }
    previewRef.current = value?.previewUrl || "";
  }, [value]);
  useEffect(() => () => previewRef.current && URL.revokeObjectURL(previewRef.current), []);

  const handleFile = (file) => {
    if (!file) return;
    setError("");
    if (!REFERENCE_TYPES.includes(file.type)) {
      setError("Please upload a PNG, JPG or WebP image.");
      return;
    }
    if (file.size > REFERENCE_MAX_BYTES) {
      setError("Image must be 20 MB or smaller.");
      return;
    }
    onChange({ file, previewUrl: URL.createObjectURL(file), name: file.name });
  };

  const pick = () => !disabled && inputRef.current?.click();

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-gray-900">{label}</span>
      <span className="text-xs text-gray-500">{hint}</span>

      {value ? (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-2.5">
          <div className="h-16 w-28 flex-none overflow-hidden rounded-lg bg-gray-200">
            <img src={value.previewUrl} alt={label} className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{value.name}</p>
            <p className="text-xs text-gray-500">{(value.file.size / (1024 * 1024)).toFixed(1)} MB</p>
          </div>
          <div className="flex flex-none items-center gap-1">
            <button
              type="button"
              onClick={pick}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
            >
              <RefreshCw size={13} /> Replace
            </button>
            <button
              type="button"
              onClick={() => {
                setError("");
                onChange(null);
              }}
              disabled={disabled}
              aria-label={`Remove ${label.toLowerCase()}`}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
            >
              <X size={15} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={disabled}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFile(e.dataTransfer.files?.[0]);
          }}
          className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors disabled:opacity-50 ${
            dragging ? "border-indigo-500 bg-indigo-50" : "border-gray-300 bg-gray-50 hover:border-indigo-400"
          }`}
        >
          <ImageUp size={22} className="text-indigo-600" />
          <span className="text-sm font-medium text-gray-900">Click to upload or drag an image here</span>
          <span className="text-xs text-gray-500">PNG, JPG or WebP · up to 20 MB</span>
        </button>
      )}

      <input
        ref={inputRef}
        data-testid={testId}
        type="file"
        accept={REFERENCE_TYPES.join(",")}
        hidden
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
